import { ChillingCheckSchema } from './chillingCheck.js';
import { z } from 'zod';

export const EventKindSchema = z.enum([
  'temperature_reading',
  'cleaning_done',
  'cleaning_voided',
  'label_printed',
  'label_voided',
  'stock_in',
  'stock_out',
  'document_received',
  'expiration_check',
  'probe_alert',
  'ai_vision',
  'sync_conflict',
  'audit_anomaly',
  'conformity_check',
  'shipment_dispatched',
  'shipment_voided',
  'production_day_closed',
  'production_day_verified',
  'production_day_reopened',
  'production_input_trace',
  // Correzione manuale append-only del costo food. Non è un movimento stock:
  // il read model foods sceglie deterministicamente tra acquisti e rettifiche.
  'cost_adjustment',
  // T-278 (collettiva): impegno (riserva) di stock all'avvio di una preparazione
  // del piano di produzione. Lo scarico effettivo alla chiusura resta 'stock_out';
  // il rilascio della riserva è uno 'stock_reserved' con reserve_released=true
  // (compensativo, stesso pattern di void_compensation).
  'stock_reserved',
]);
export type EventKind = z.infer<typeof EventKindSchema>;

export const ProbeTierSchema = z.enum(['certified', 'monitoring', 'manual', 'compliance']);
export type ProbeTier = z.infer<typeof ProbeTierSchema>;

export const OperationalEvidencePhotoSchema = z.object({
  storage_path: z.string().min(1).max(500),
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  mime: z.enum(['image/jpeg', 'image/png', 'image/webp']),
  size_bytes: z.number().int().min(1).max(10_485_760),
  captured_at: z.string().datetime({ offset: true }),
});
export type OperationalEvidencePhoto = z.infer<typeof OperationalEvidencePhotoSchema>;

export const TemperatureReadingPayloadSchema = z.object({
  storage_unit_id: z.string().uuid(),
  probe_id: z.string().uuid().nullable(),
  temperature_c: z.number().min(-80).max(150),
  humidity_pct: z.number().min(0).max(100).nullable().optional(),
  tier: ProbeTierSchema,
  source: z.enum(['ble', 'mqtt_cloud', 'manual_osa', 'manual_staff', 'manual_check']),
  osa_user_id: z.string().uuid().nullable().optional(),
  instrument_serial: z.string().max(100).nullable().optional(),
  signature: z.string().nullable().optional(),
  notes: z.string().max(500).nullable().optional(),
  // T-333 (recupero postumo): true = controllo eseguito un giorno passato ma
  // registrato in ritardo. `client_ts` è retrodatato al giorno reale + ora
  // dichiarata; `operational_date` (YYYY-MM-DD locale) è il giorno a cui il
  // controllo si riferisce. Additivo + opzionale → assente su tutti gli eventi
  // pre-feature e correnti. Nessun .default()/.strict() → canonicalJson dei
  // payload storici invariato → hash chain intatta (invarianti #4/#5).
  recorded_late: z.boolean().optional(),
  operational_date: z.string().date().nullable().optional(),
  operational_time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).nullable().optional(),
  conformity: z.enum(['ok', 'ko']).optional(),
  evidence_photo: OperationalEvidencePhotoSchema.optional(),
});
export type TemperatureReadingPayload = z.infer<typeof TemperatureReadingPayloadSchema>;

export const CleaningDonePayloadSchema = z.object({
  schedule_id: z.string().uuid(),
  equipment_id: z.string().uuid().nullable(),
  product_id: z.string().uuid().nullable(),
  performed_by_user_id: z.string().uuid(),
  operator_name: z.string().trim().min(1).max(200).nullable().optional(),
  notes: z.string().max(500).nullable().optional(),
  photo_file_id: z.string().nullable().optional(),
  evidence_photo: OperationalEvidencePhotoSchema.optional(),
  // T-333 (recupero postumo): vedi nota su TemperatureReadingPayloadSchema.
  // Additivo + opzionale, nessun .default()/.strict() → hash chain intatta.
  recorded_late: z.boolean().optional(),
  operational_date: z.string().date().nullable().optional(),
  operational_time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).nullable().optional(),
});
export type CleaningDonePayload = z.infer<typeof CleaningDonePayloadSchema>;

export const LabelPrintedPayloadSchema = z.object({
  template_id: z.string().uuid(),
  // Le stampanti Bluetooth/USB sono configurazioni del dispositivo e non
  // necessariamente master sincronizzati. `printer_id` resta per i master
  // server legacy; le stampanti locali viaggiano come snapshot verificabile.
  printer_id: z.string().uuid().nullable().optional(),
  printer_device: z.object({
    name: z.string().trim().min(1).max(200),
    driver: z.enum([
      'brother_ql', 'zpl', 'escpos', 'tspl', 'cpcl', 'niimbot',
      'preview', 'system', 'qz',
    ]),
    connection: z.enum(['bluetooth', 'ble', 'wifi', 'usb']),
  }).optional(),
  preset: z.enum([
    'takeaway',
    'decongelato',
    'lavorato',
    'ingrediente_aperto',
    'ricetta',
    'tracciabilita_lotto',
    // T-229 (collettiva): etichetta CAMPIONE aggiuntiva del pasto campione
    // (conservazione 72h in cella).
    'campione',
  ]),
  data: z.record(z.unknown()).superRefine((data, ctx) => {
    if (data.chilling_check !== undefined && !ChillingCheckSchema.safeParse(data.chilling_check).success) ctx.addIssue({ code: 'custom', path: ['chilling_check'], message: 'Misura finale abbattimento non valida' });
  }),
  copies: z.number().int().positive().default(1),
  // Killer features:
  // lp_code: identificatore univoco human-readable (LP-{epoch36}{rand36}) stampato sull'etichetta
  // e usato per ristampa/audit. Può essere nullo per eventi pre-feature (backwards compat).
  lp_code: z.string().regex(/^LP-[A-Z0-9]+$/i).max(40).nullable().optional(),
  // expires_at: scadenza calcolata al momento della stampa (override default su shelf-life ricetta).
  expires_at: z.string().datetime().nullable().optional(),
  // produced_at: data lavorazione esplicita (può differire da client_ts in caso di backdate).
  produced_at: z.string().datetime().nullable().optional(),
}).refine(
  (payload) => payload.printer_id != null || payload.printer_device != null,
  { message: 'printer_id or printer_device is required' },
);
export type LabelPrintedPayload = z.infer<typeof LabelPrintedPayloadSchema>;

// T-109: evento append-only emesso quando l'utente "annulla" un'etichetta dallo
// storico. Il record originale label_printed RESTA in DB (regola sacra
// append-only di CLAUDE.md). La UI dello storico filtra via le label_printed
// che hanno un label_voided corrispondente. Propaga a tutti i dispositivi.
export const CleaningVoidedPayloadSchema = z.object({
  schedule_id: z.string().uuid(),
  original_client_uuids: z.array(z.string().uuid()).min(1).max(200),
  reason: z.literal('user_cancel'),
});

export const LabelVoidedPayloadSchema = z.object({
  original_event_id: z.string().uuid(),
  reason: z.enum(['user_cancel', 'manual_repair', 'duplicate']).default('user_cancel'),
  voided_at: z.string().datetime(),
});
export type LabelVoidedPayload = z.infer<typeof LabelVoidedPayloadSchema>;

// Generatore codice lotto compatto: 11 caratteri totali (prefisso + 3 timestamp +
// 5 random). Pensato per stare dentro l'etichetta 50×30 mm senza
// sovrapporsi al QR. Le 3 cifre meno significative dell'epoch in
// secondi base36 danno un debole ordinamento (ciclo ~13 h); 5 caratteri
// random base36 → 60 M combinazioni: collisioni trascurabili.
function compactLotSuffix(now: Date): string {
  const t = Math.floor(now.getTime() / 1000)
    .toString(36)
    .toUpperCase()
    .slice(-3)
    .padStart(3, '0');
  const r = Math.floor(Math.random() * 36 ** 5)
    .toString(36)
    .toUpperCase()
    .padStart(5, '0');
  return `${t}${r}`;
}

export function generateLpCode(now: Date = new Date()): string {
  return `LP-${compactLotSuffix(now)}`;
}

/** Codice visibile del lotto di materia prima trasformata. */
export function generateMaterialLotCode(
  transformation: 'open' | 'thaw',
  now: Date = new Date(),
): string {
  return `${transformation === 'thaw' ? 'SC' : 'AP'}-${compactLotSuffix(now)}`;
}

// T-303 (collettiva, pilota Bluecoop) — lotto = data di produzione, NON casuale.
// Per le pre-stampe della ristorazione collettiva il lotto deve essere
// deterministico dalla giornata di produzione (tutte le etichette dello stesso
// giorno condividono lo STESSO lotto) e riportare SOLO la data, senza
// caratteri casuali. Formato interno `LP-DDMMYYYY`: mantiene il prefisso `LP-`
// che il regex di `lp_code` (`^LP-[A-Z0-9]+$`) richiede — così resta valido
// ovunque (Zod label_printed, storico, trace) senza toccare il regex. In
// etichetta il valore viene mostrato come `DD/MM/YYYY` (binding sulla data o
// filtro renderer `lotdate`). Usa la data LOCALE (il giorno scelto
// dall'operatore), non UTC, per evitare slittamenti di un giorno.
export function collettivaLotCode(date: Date): string {
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = String(date.getFullYear());
  return `LP-${dd}${mm}${yyyy}`;
}

// Priorità: durata configurata in minuti > giorni > default di conservazione.
interface RecipeShelfLifePolicy {
  default_shelf_life_minutes?: number | null;
  shelf_life_days?: number | null;
  freezing_enabled?: boolean;
  chilling_enabled?: boolean;
  chilling_sign?: 'positive' | 'negative' | null;
}

export function recipeShelfLifeMinutes(recipe?: RecipeShelfLifePolicy | null): number {
  if (recipe?.default_shelf_life_minutes && recipe.default_shelf_life_minutes > 0) {
    return recipe.default_shelf_life_minutes;
  }
  if (recipe?.shelf_life_days && recipe.shelf_life_days > 0) {
    return recipe.shelf_life_days * 24 * 60;
  }
  // L’abbattimento alimenta la sua scheda, non decide la conservazione.
  return recipe?.freezing_enabled === true ? 90 * 24 * 60 : 72 * 60;
}

export function computeLabelExpiration(
  preparedAt: Date,
  recipe?: RecipeShelfLifePolicy | null,
): Date {
  return new Date(preparedAt.getTime() + recipeShelfLifeMinutes(recipe) * 60_000);
}

export const StockMovementPayloadSchema = z.object({
  food_id: z.string().uuid(),
  qty: z.number(),
  uom: z.string().max(10),
  batch_lot: z.string().max(100).nullable(),
  expiration_date: z.string().date().nullable(),
  supplier_id: z.string().uuid().nullable(),
  document_id: z.string().uuid().nullable(),
  storage_unit_id: z.string().uuid().nullable(),
  // Campi opzionali storicamente presenti nei payload reali (il validator
  // Zod di default li stripperebbe senza errore; dichiararli li preserva):
  // - source_production_id: scarico ingredienti / carico prodotto finito
  //   collegato a una lavorazione (LabelsPage).
  // - source_shipment_id (T-221): movimento generato dall'emissione di un
  //   DDT/trasferimento collettiva.
  // - business_id (T-221): sede del movimento, ridondante con la colonna
  //   events.business_id ma utile nei payload storicizzati.
  // - manual_entry / lot_closed / notify_expiration: flag del piano base.
  source_production_id: z.string().uuid().nullable().optional(),
  source_shipment_id: z.string().uuid().nullable().optional(),
  business_id: z.string().uuid().nullable().optional(),
  manual_entry: z.boolean().optional(),
  lot_closed: z.boolean().optional(),
  notify_expiration: z.boolean().optional(),
  operator_name: z.string().max(200).nullable().optional(),
  supplier_sku: z.string().max(200).nullable().optional(),
  document_number: z.string().max(200).nullable().optional(),
  document_date: z.string().date().nullable().optional(),
  photo_path: z.string().max(2_048).nullable().optional(),
  is_frozen: z.boolean().optional(),
  pack_factor_applied: z.boolean().optional(),
  keep_current_lot: z.boolean().optional(),
  // T-221: carico del prodotto finito generato da una stampa etichetta.
  finished_good: z.boolean().optional(),
  // T-228: evento compensativo emesso annullando un DDT (ripristina la giacenza
  // append-only senza delete).
  void_compensation: z.boolean().optional(),
  // T-278 (collettiva): riga del piano di produzione che ha originato il
  // movimento (riserva all'avvio / scarico alla chiusura).
  source_production_plan_item_id: z.string().uuid().nullable().optional(),
  // T-278: su kind='stock_reserved', true = rilascio della riserva
  // (compensativo, alla chiusura), false/assente = impegno.
  reserve_released: z.boolean().optional(),
  // T-284 (Food Cost): costo del carico. Additivo + opzionale → presente SOLO
  // sugli stock_in di ACQUISTO (DDT/fattura o acquisto manuale), assente sulle
  // correzioni stock-only, sui movimenti interni e su TUTTI gli eventi
  // pre-feature. Le correzioni conservano il valore in `stock_unit_cost` senza
  // creare una nuova osservazione di prezzo. Assenza = costo non noto (MAI 0
  // falso). Hashato solo nei NUOVI
  // payload: canonicalJson degli eventi storici resta invariato → hash chain
  // intatta (invarianti #4/#5). NB: NESSUN .default() materializzante e NESSUN
  // .strict()/.passthrough() su questo schema, per non alterare il
  // canonicalJson di payload già scritti.
  // - unit_price/line_total: valori grezzi dal documento (sulla uom documento).
  // - currency: valuta del documento (ISO 4217), default applicato dal writer.
  // - unit_cost: costo per unità sulla uom CANONICA del movimento (T-232).
  // - cost_source: provenienza del costo (vedi domain/cost.ts CostSource).
  unit_price: z.number().nullable().optional(),
  line_total: z.number().nullable().optional(),
  currency: z.string().length(3).nullable().optional(),
  unit_cost: z.number().nullable().optional(),
  cost_source: z.enum(['document', 'manual', 'carried_lot']).nullable().optional(),
  // T-481: coordinate commerciali immutabili + derivazione stock. I campi
  // legacy qty/uom/unit_cost restano per i client stable; i nuovi writer
  // dichiarano entrambe le coordinate e il comando gestionale che le ha create.
  source_qty: z.number().nonnegative().optional(),
  source_uom: z.string().min(1).max(10).optional(),
  source_unit_price: z.number().nonnegative().nullable().optional(),
  source_line_total: z.number().nonnegative().nullable().optional(),
  source_currency: z.string().length(3).optional(),
  stock_qty: z.number().nonnegative().optional(),
  stock_uom: z.string().min(1).max(10).optional(),
  stock_unit_cost: z.number().nonnegative().nullable().optional(),
  conversion_factor: z.number().positive().optional(),
  conversion_source: z.enum([
    'identity',
    'metric',
    'document_pack',
    'document_weight',
    'supplier_alias',
    'user_confirmed',
    'carried',
  ]).optional(),
  command_id: z.string().uuid().optional(),
  command_version: z.literal(1).optional(),
  command_seq: z.number().int().positive().optional(),
  movement_class: z.enum([
    'receive',
    'inventory_adjustment',
    'lot_state',
    'correction',
    'consume',
    'dispatch',
    'uom_rebase',
    'transfer',
    'production',
    'reverse',
  ]).optional(),
  economic_effect: z.enum(['purchase', 'stock_only', 'internal', 'reversal']).optional(),
  lot_action: z.enum(['updated_active', 'recorded_only']).optional(),
  lot_state_action: z.enum(['register', 'close', 'update_metadata']).optional(),
  // T-539: una rettifica da conteggio fisico su lotto storico non e' un nuovo
  // acquisto. Queste coordinate descrivono il conteggio osservato (per esempio
  // 12 colli x 5,3 kg) mentre stock_qty resta nella UdM canonica del food.
  // Sono dichiarate esplicitamente per non perderle quando il payload viene
  // letto/sincronizzato da un client successivo.
  historical_lot_recount: z.literal(true).optional(),
  // Legato dal writer a una capability privata e monouso: non e' un flag
  // generico che un client possa usare per riaprire un lotto chiuso.
  historical_lot_recount_request_id: z.string().uuid().optional(),
  historical_lot_recount_source_event_id: z.string().uuid().optional(),
  historical_lot_recount_source_document_id: z.string().uuid().optional(),
  historical_lot_recount_source_document_store: z.enum([
    'public',
    'fiscal',
  ]).optional(),
  historical_lot_recount_source_qty: z.number().positive().optional(),
  historical_lot_recount_source_uom: z.string().min(1).max(10).optional(),
  historical_lot_recount_conversion_factor: z.number().positive().optional(),
  historical_lot_recount_conversion_source: z.enum([
    'identity',
    'metric',
    'document_pack',
    'document_weight',
    'supplier_alias',
    'user_confirmed',
    'carried',
  ]).optional(),
  historical_lot_recount_note: z.string().min(1).max(500).optional(),
  historical_lot_recount_expected_event_count: z.number().int().positive().optional(),
  historical_lot_recount_expected_last_event_client_uuid: z.string().uuid().optional(),
  // `origin_*` are copied only by the T-539 server RPC from the immutable
  // document-backed receipt.  They deliberately retain the old commercial
  // coordinates as audit provenance, without turning a physical recount into
  // a new purchase or assigning a fictitious carrying value to zero stock.
  historical_lot_recount_origin_supplier_id: z.string().uuid().nullable().optional(),
  historical_lot_recount_origin_storage_unit_id: z.string().uuid().nullable().optional(),
  historical_lot_recount_origin_source_qty: z.number().positive().optional(),
  historical_lot_recount_origin_source_uom: z.string().min(1).max(10).optional(),
  historical_lot_recount_origin_source_unit_price: z.number().nonnegative().nullable().optional(),
  historical_lot_recount_origin_source_line_total: z.number().nonnegative().nullable().optional(),
  historical_lot_recount_origin_stock_qty: z.number().positive().optional(),
  historical_lot_recount_origin_stock_uom: z.string().min(1).max(10).optional(),
  historical_lot_recount_origin_stock_unit_cost: z.number().nonnegative().nullable().optional(),
  historical_lot_recount_origin_source_currency: z.string().length(3).optional(),
  historical_lot_recount_origin_conversion_factor: z.number().positive().optional(),
  historical_lot_recount_origin_conversion_source: z.enum([
    'identity',
    'metric',
    'document_pack',
    'document_weight',
    'supplier_alias',
    'user_confirmed',
    'carried',
  ]).optional(),
  correction_note: z.string().max(500).optional(),
  correction_leg: z.enum(['source', 'target']).optional(),
  correction_root_event_id: z.string().uuid().optional(),
  corrects_event_id: z.string().uuid().optional(),
  corrects_command_id: z.string().uuid().optional(),
  corrects_command_seq: z.number().int().positive().optional(),
  corrected_from_qty: z.number().positive().optional(),
  corrected_from_uom: z.string().min(1).max(10).optional(),
  corrected_from_lot: z.string().max(100).nullable().optional(),
  corrected_from_expiration_date: z.string().date().nullable().optional(),
  corrected_to_qty: z.number().positive().optional(),
  corrected_to_uom: z.string().min(1).max(10).optional(),
  corrected_to_lot: z.string().max(100).nullable().optional(),
  corrected_to_expiration_date: z.string().date().nullable().optional(),
  expected_balance_qty: z.number().nonnegative().optional(),
  expected_balance_reserved_qty: z.number().nonnegative().optional(),
  expected_balance_uom: z.string().min(1).max(10).optional(),
  expected_balance_event_count: z.number().int().positive().optional(),
  expected_balance_last_event_ref: z.string().uuid().optional(),
  expected_balance_last_server_rev: z.number().int().positive().optional(),
  // Il server event id resta l'identità audit remota; client_uuid è la PK del
  // mirror events_local. I reverse nuovi conservano entrambe per evitare
  // confronti fra namespace incompatibili nei consumer offline.
  reverses_event_id: z.string().uuid().optional(),
  reverses_event_client_uuid: z.string().uuid().optional(),
});
export type StockMovementPayload = z.infer<typeof StockMovementPayloadSchema>;

// Controllo HACCP al ricevimento merci (T-061). Una sola domanda globale:
// "tutta la merce è conforme rispetto alle 6 voci del disclaimer?" Sì o No.
// Se No, l'utente è obbligato a compilare una nota testuale. L'import in
// magazzino procede comunque (HACCP richiede la PROVA del controllo, non
// l'impedimento del carico). Le voci del disclaimer sono salvate nel
// payload per audit-proof: se il testo del disclaimer cambia in futuro,
// gli event passati restano leggibili in autonomia.
export const ConformityCheckPayloadSchema = z.object({
  document_id: z.string().uuid(),
  conform: z.boolean(),
  notes: z.string().max(2000).nullable(),
  checked_by_user_id: z.string().uuid(),
  checked_at: z.string().datetime(),
  disclaimer_items: z.array(z.string().max(200)).max(20),
  // T-291: temperatura di ricevimento per singolo articolo (modalità per-item,
  // gated su tenants.receiving_temp_per_item). Opzionale e additivo: in modalità
  // generica resta assente e il MOD 01 mostra il solo flag conformità. Letto
  // dall'export-carico-month per riga (food_id → temp_c).
  item_temps: z.array(z.object({ food_id: z.string().uuid(), temp_c: z.number() })).max(500).optional(),
});
export type ConformityCheckPayload = z.infer<typeof ConformityCheckPayloadSchema>;

// Voci standard del disclaimer HACCP mostrate all'utente al ricevimento.
// Mantenute in shared per essere consumate sia dalla UI mobile sia dalla
// edge function (qualora servisse renderle server-side, es. in trace-page).
export const CONFORMITY_DISCLAIMER_ITEMS: readonly string[] = [
  'Confezione integra',
  'Temperatura corretta (refrigerati/surgelati)',
  'Etichettatura leggibile e scadenze valide',
  'Lotti presenti e tracciabili',
  'Quantità/prodotti corrispondono al DDT',
  'Aspetto e odore normali',
] as const;

export const DocumentReceivedPayloadSchema = z.object({
  document_id: z.string().uuid(),
  supplier_piva: z.string(),
  total_amount: z.number(),
  line_count: z.number().int().nonnegative(),
  via_provider: z.string(),
  sdi_id: z.string(),
});
export type DocumentReceivedPayload = z.infer<typeof DocumentReceivedPayloadSchema>;

export const ProbeAlertPayloadSchema = z.object({
  probe_id: z.string().uuid(),
  storage_unit_id: z.string().uuid(),
  rule_id: z.string(),
  severity: z.enum(['info', 'warning', 'critical']),
  message: z.string().max(500),
  window_start: z.string().datetime(),
  window_end: z.string().datetime(),
  samples_count: z.number().int().nonnegative(),
});
export type ProbeAlertPayload = z.infer<typeof ProbeAlertPayloadSchema>;

export const AiVisionPayloadSchema = z.object({
  kind: z.enum(['invoice', 'ddt', 'food_package', 'raw']),
  image_hash: z.string().length(64),
  model: z.string(),
  latency_ms: z.number().int().nonnegative(),
  tokens_in: z.number().int().nonnegative().nullable(),
  tokens_out: z.number().int().nonnegative().nullable(),
  cost_estimate_eur: z.number().nonnegative().nullable(),
  cached: z.boolean(),
});
export type AiVisionPayload = z.infer<typeof AiVisionPayloadSchema>;

// T-215 (collettiva): emissione di un documento di trasporto (DDT pasti verso
// scuole/ospedali o trasferimento interno magazzino→impianto). Emesso dal
// client SUBITO DOPO che la RPC issue_shipment() ha assegnato il numero
// progressivo. Snapshot delle righe nel payload: l'evento resta leggibile in
// autonomia anche se il master shipments cambia (append-only audit).
export const ShipmentDispatchedPayloadSchema = z.object({
  shipment_id: z.string().uuid(),
  kind: z.enum(['ddt', 'trasferimento']),
  doc_number: z.number().int().positive(),
  doc_year: z.number().int(),
  destination_id: z.string().uuid().nullable().optional(),
  destination_name: z.string().max(200).nullable().optional(),
  to_business_id: z.string().uuid().nullable().optional(),
  line_count: z.number().int().nonnegative(),
  lines_summary: z
    .array(
      z.object({
        description: z.string().max(300),
        batch_lot: z.string().max(100),
        qty: z.number(),
        uom: z.string().max(20),
      }),
    )
    .max(200),
  departure_at: z.string().datetime().nullable().optional(),
  temp_hot_c: z.number().nullable().optional(),
  temp_cold_c: z.number().nullable().optional(),
  vehicle_plate: z.string().max(20).nullable().optional(),
  carrier_name: z.string().max(200).nullable().optional(),
});
export type ShipmentDispatchedPayload = z.infer<typeof ShipmentDispatchedPayloadSchema>;

export const ShipmentVoidedPayloadSchema = ShipmentDispatchedPayloadSchema.extend({
  reason: z.string().trim().min(1).max(500),
  voided_at: z.string().datetime(),
  stock_void_command_id: z.string().uuid().nullable(),
});
export type ShipmentVoidedPayload = z.infer<typeof ShipmentVoidedPayloadSchema>;

// T-215 (collettiva): chiusura della scheda di produzione giornaliera
// (= "rilascio" del batch record, BRCGS 5.7.1). Append-only: riaperture o
// correzioni passano da un nuovo evento, mai da update del precedente.
export const ProductionDayClosedPayloadSchema = z.object({
  // Presente sugli eventi emessi dal percorso atomico V1. Opzionale soltanto
  // per poter continuare a leggere gli eventi storici creati prima di T-481.
  request_id: z.string().uuid().optional(),
  production_day_id: z.string().uuid(),
  sheet_date: z.string().date(),
  productions_count: z.number().int().nonnegative(),
  meals_total: z.number().int().nonnegative().nullable().optional(),
  sample_meal_taken: z.boolean(),
  closed_by_user_id: z.string().uuid(),
  business_id: z.string().uuid().optional(),
});
export type ProductionDayClosedPayload = z.infer<typeof ProductionDayClosedPayloadSchema>;

export const ProductionDayVerifiedPayloadSchema = z.object({
  request_id: z.string().uuid().optional(),
  production_day_id: z.string().uuid(),
  sheet_date: z.string().date(),
  closed_at: z.string().datetime(),
  closed_by_user_id: z.string().uuid(),
  verified_by_user_id: z.string().uuid(),
  reopen_count: z.number().int().nonnegative(),
  business_id: z.string().uuid(),
});
export type ProductionDayVerifiedPayload = z.infer<
  typeof ProductionDayVerifiedPayloadSchema
>;

export const ProductionDayReopenedPayloadSchema = z.object({
  request_id: z.string().uuid().optional(),
  production_day_id: z.string().uuid(),
  sheet_date: z.string().date(),
  reopened_by_user_id: z.string().uuid(),
  reopen_count: z.number().int().positive(),
  business_id: z.string().uuid(),
});
export type ProductionDayReopenedPayload = z.infer<
  typeof ProductionDayReopenedPayloadSchema
>;

export const CostAdjustmentPayloadSchema = z.object({
  food_id: z.string().uuid(),
  unit_cost: z.number().finite().nonnegative().nullable(),
  cost_uom: z.string().trim().min(1).max(10).nullable(),
  currency: z.string().regex(/^[A-Z]{3}$/).nullable(),
  effective_at: z.string().datetime(),
  reason: z.enum(['manual_food_cost_fix', 'recipe_cost_missing']),
}).superRefine((payload, ctx) => {
  if (payload.unit_cost !== null && payload.cost_uom === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['cost_uom'],
      message: 'Una rettifica con costo richiede la relativa UdM',
    });
  }
  if (payload.unit_cost !== null && payload.currency === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['currency'],
      message: 'Una rettifica con costo richiede la valuta',
    });
  }
  if (
    payload.unit_cost === null
    && (payload.cost_uom !== null || payload.currency !== null)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['unit_cost'],
      message: 'La cancellazione del costo deve lasciare UdM e valuta nulle',
    });
  }
});
export type CostAdjustmentPayload = z.infer<typeof CostAdjustmentPayloadSchema>;

const ProductionInputTraceMatchMethodSchema = z.enum([
  'normalized_lot',
  'normalized_lot_expiry',
  'operator_selected',
  'not_found',
  'operator_unmatched',
]);
const ProductionInputTraceUuidPattern =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ProductionInputTracePhotoFilenamePattern =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.(?:jpe?g|png|webp)$/i;

export const ProductionInputTracePayloadV1Schema = z.object({
  schema: z.literal('trackeat.production-input-trace'),
  version: z.literal(1),
  production_id: z.string().uuid(),
  input_id: z.string().uuid(),
  capture_id: z.string().uuid(),
  ingredient: z.object({
    food_id: z.string().uuid(),
    food_name: z.string().trim().min(1).max(200),
    qty_used: z.number().finite().min(0).max(1_000_000_000),
    uom: z.string().trim().min(1).max(20),
    allergens: z.array(z.string().trim().min(1).max(100)).max(50),
    expiration_date: z.string().date().nullable(),
  }).strict(),
  photo_paths: z.array(z.string().trim().min(1).max(2048)).min(1).max(3),
  captured_at: z.string().datetime({ offset: true }),
  extraction: z.object({
    batch_lot: z.string().trim().min(1).max(100),
    expiration_date: z.string().date().nullable(),
    confidence: z.number().finite().min(0).max(1).nullable(),
  }).strict(),
  match: z.object({
    status: z.enum(['matched', 'not_found']),
    method: ProductionInputTraceMatchMethodSchema,
    batch_lot: z.string().trim().min(1).max(100).nullable(),
    supplier_id: z.string().uuid().nullable(),
    document_id: z.string().uuid().nullable(),
    source_production_id: z.string().uuid().nullable(),
  }).strict(),
  warning: z.object({
    code: z.literal('lot_not_found'),
    non_blocking: z.literal(true),
  }).strict().nullable(),
}).strict().superRefine((payload, ctx) => {
  payload.photo_paths.forEach((path, index) => {
    const segments = path.split('/');
    if (
      segments.length !== 5
      || segments.slice(0, 4).some((segment) =>
        !ProductionInputTraceUuidPattern.test(segment)
      )
      || !ProductionInputTracePhotoFilenamePattern.test(segments[4] ?? '')
      || segments[2]?.toLowerCase() !== payload.production_id.toLowerCase()
      || segments[3]?.toLowerCase() !== payload.capture_id.toLowerCase()
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['photo_paths', index],
        message: 'Percorso foto lotto non canonico o non legato alla cattura',
      });
    }
  });

  if (payload.match.status === 'matched') {
    if (payload.match.batch_lot === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['match', 'batch_lot'],
        message: 'Un lotto riconciliato deve conservare il lotto di magazzino',
      });
    }
    if (!['normalized_lot', 'normalized_lot_expiry', 'operator_selected'].includes(
      payload.match.method,
    )) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['match', 'method'],
        message: 'Metodo incompatibile con un match riuscito',
      });
    }
    if (payload.warning !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['warning'],
        message: 'Un match riuscito non deve generare un avviso',
      });
    }
    return;
  }

  if (!['not_found', 'operator_unmatched'].includes(payload.match.method)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['match', 'method'],
      message: 'Metodo incompatibile con un lotto non trovato',
    });
  }
  for (const key of ['supplier_id', 'document_id', 'source_production_id'] as const) {
    if (payload.match[key] !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['match', key],
        message: 'Un lotto non trovato non puo conservare collegamenti di magazzino',
      });
    }
  }
  if (payload.warning === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['warning'],
      message: 'Un lotto non trovato deve conservare un avviso non bloccante',
    });
  }
});

const ProductionLotPhotoIngredientV2Schema = z.object({
  input_id: z.string().uuid(),
  food_id: z.string().uuid(),
  food_name: z.string().trim().min(1).max(200),
  qty_used: z.number().finite().min(0).max(1_000_000_000),
  uom: z.string().trim().min(1).max(20),
  allergens: z.array(z.string().trim().min(1).max(100)).max(50),
  selected_stock: z.object({
    batch_lot: z.string().trim().min(1).max(100),
    expiration_date: z.string().date().nullable(),
    supplier_id: z.string().uuid().nullable(),
    document_id: z.string().uuid().nullable(),
    source_production_id: z.string().uuid().nullable(),
  }).strict().nullable(),
}).strict();

const ProductionLotPhotoManifestPhotoV2Schema = z.object({
  photo_id: z.string().uuid(),
  path: z.string().trim().min(1).max(2048),
  mime: z.enum(['image/jpeg', 'image/jpg', 'image/png', 'image/webp']),
  sha256: z.string().regex(/^[a-f0-9]{64}$/i),
  size_bytes: z.number().int().min(1).max(5_000_000),
}).strict();

const ProductionLotPhotoCaptureV2Schema = z.object({
  capture_id: z.string().uuid(),
  captured_at: z.string().datetime({ offset: true }),
  photos: z.array(ProductionLotPhotoManifestPhotoV2Schema).min(1).max(3),
}).strict();

export const ProductionLotPhotoCapturedPayloadV2Schema = z.object({
  schema: z.literal('trackeat.production-lot-photo-trace'),
  version: z.literal(2),
  stage: z.literal('captured'),
  production_id: z.string().uuid(),
  batch_id: z.string().uuid(),
  captured_at: z.string().datetime({ offset: true }),
  warehouse_cutoff_server_rev: z.number().int().min(0),
  manifest_digest: z.string().regex(/^[a-f0-9]{64}$/i),
  ingredients: z.array(ProductionLotPhotoIngredientV2Schema).min(1).max(1_000),
  captures: z.array(ProductionLotPhotoCaptureV2Schema).min(1).max(20),
}).strict().superRefine((payload, ctx) => {
  const captureIds = new Set<string>();
  const photoPaths = new Set<string>();
  let totalPhotos = 0;
  payload.captures.forEach((capture, captureIndex) => {
    if (captureIds.has(capture.capture_id.toLowerCase())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['captures', captureIndex, 'capture_id'],
        message: 'Identificatore cattura duplicato',
      });
    }
    captureIds.add(capture.capture_id.toLowerCase());
    totalPhotos += capture.photos.length;
    capture.photos.forEach((photo, photoIndex) => {
      const segments = photo.path.split('/');
      if (
        segments.length !== 5
        || segments.slice(0, 4).some((segment) =>
          !ProductionInputTraceUuidPattern.test(segment)
        )
        || segments[2]?.toLowerCase() !== payload.production_id.toLowerCase()
        || segments[3]?.toLowerCase() !== capture.capture_id.toLowerCase()
        || !ProductionInputTracePhotoFilenamePattern.test(segments[4] ?? '')
        || segments[4]?.split('.')[0]?.toLowerCase() !== photo.photo_id.toLowerCase()
        || photoPaths.has(photo.path)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['captures', captureIndex, 'photos', photoIndex, 'path'],
          message: 'Percorso foto batch non canonico o duplicato',
        });
      }
      photoPaths.add(photo.path);
    });
  });
  if (totalPhotos > 20) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['captures'],
      message: 'Il batch supera il limite di 20 foto',
    });
  }
});

export const ProductionLotPhotoResolvedPayloadV2Schema = z.object({
  schema: z.literal('trackeat.production-lot-photo-trace'),
  version: z.literal(2),
  stage: z.literal('resolved'),
  production_id: z.string().uuid(),
  batch_id: z.string().uuid(),
  capture_id: z.string().uuid(),
  job_id: z.string().uuid(),
  photo_paths: z.array(z.string().trim().min(1).max(2048)).min(1).max(3),
  captured_at: z.string().datetime({ offset: true }),
  extraction: z.object({
    name: z.string().trim().min(1).max(200).nullable(),
    brand: z.string().trim().min(1).max(200).nullable(),
    batch_lot: z.string().trim().min(1).max(100).nullable(),
    expiration_date: z.string().date().nullable(),
    confidence: z.number().finite().min(0).max(1),
  }).strict(),
  ingredient_match: z.object({
    status: z.enum(['matched', 'ambiguous', 'unassigned']),
    input_id: z.string().uuid().nullable(),
    score: z.number().finite().min(0).max(1).nullable(),
    candidates: z.array(z.string().uuid()).max(20),
  }).strict(),
  ingredient: ProductionLotPhotoIngredientV2Schema.nullable(),
  warehouse_match: z.object({
    status: z.enum(['matched', 'not_found_at_cutoff', 'ambiguous', 'unreadable']),
    batch_lot: z.string().trim().min(1).max(100).nullable(),
    expiration_date: z.string().date().nullable(),
    supplier_id: z.string().uuid().nullable(),
    document_id: z.string().uuid().nullable(),
    source_production_id: z.string().uuid().nullable(),
    cutoff_server_rev: z.number().int().min(0),
  }).strict(),
  stock_check: z.object({
    status: z.enum(['confirmed', 'reconciliation_required', 'not_applicable']),
    selected_batch_lot: z.string().trim().min(1).max(100).nullable(),
    observed_batch_lot: z.string().trim().min(1).max(100).nullable(),
  }).strict(),
  warnings: z.array(z.string().trim().min(1).max(100)).max(20),
}).strict().superRefine((payload, ctx) => {
  payload.photo_paths.forEach((path, index) => {
    const segments = path.split('/');
    if (
      segments.length !== 5
      || segments.slice(0, 4).some((segment) =>
        !ProductionInputTraceUuidPattern.test(segment)
      )
      || segments[2]?.toLowerCase() !== payload.production_id.toLowerCase()
      || segments[3]?.toLowerCase() !== payload.capture_id.toLowerCase()
      || !ProductionInputTracePhotoFilenamePattern.test(segments[4] ?? '')
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['photo_paths', index],
        message: 'Percorso foto risolta non canonico',
      });
    }
  });
  const ingredientMatched = payload.ingredient_match.status === 'matched';
  if (ingredientMatched !== (payload.ingredient_match.input_id !== null && payload.ingredient !== null)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['ingredient_match'],
      message: 'Il match ingrediente non e coerente con lo snapshot',
    });
  }
  if (
    payload.warehouse_match.status !== 'matched'
    && (
      payload.warehouse_match.supplier_id !== null
      || payload.warehouse_match.document_id !== null
      || payload.warehouse_match.source_production_id !== null
    )
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['warehouse_match'],
      message: 'Un lotto non riconciliato non puo avere una provenienza',
    });
  }
  if (
    payload.stock_check.status === 'confirmed'
    && (payload.warehouse_match.status !== 'matched' || !ingredientMatched)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['stock_check', 'status'],
      message: 'Lo stock puo essere confermato solo dopo entrambi i match',
    });
  }
});

export const ProductionInputTracePayloadSchema = z.union([
  ProductionInputTracePayloadV1Schema,
  ProductionLotPhotoCapturedPayloadV2Schema,
  ProductionLotPhotoResolvedPayloadV2Schema,
]);
export type ProductionInputTracePayload = z.infer<
  typeof ProductionInputTracePayloadSchema
>;

export const EventPayloadByKind = {
  temperature_reading: TemperatureReadingPayloadSchema,
  cleaning_done: CleaningDonePayloadSchema,
  cleaning_voided: CleaningVoidedPayloadSchema,
  label_printed: LabelPrintedPayloadSchema,
  label_voided: LabelVoidedPayloadSchema,
  stock_in: StockMovementPayloadSchema,
  stock_out: StockMovementPayloadSchema,
  stock_reserved: StockMovementPayloadSchema,
  document_received: DocumentReceivedPayloadSchema,
  conformity_check: ConformityCheckPayloadSchema,
  expiration_check: z.object({
    food_id: z.string().uuid(),
    batch_lot: z.string().nullable(),
    expiration_date: z.string().date(),
    action: z.enum(['ok', 'discard', 'use_first']),
  }),
  probe_alert: ProbeAlertPayloadSchema,
  ai_vision: AiVisionPayloadSchema,
  sync_conflict: z.object({
    table: z.string(),
    row_id: z.string(),
    client_patch: z.record(z.unknown()),
    server_value: z.record(z.unknown()),
    resolution: z.enum(['lww_server', 'lww_client', 'manual']),
  }),
  audit_anomaly: z.object({
    anomaly_kind: z.string(),
    detected_at: z.string().datetime(),
    details: z.record(z.unknown()),
  }),
  shipment_dispatched: ShipmentDispatchedPayloadSchema,
  shipment_voided: ShipmentVoidedPayloadSchema,
  production_day_closed: ProductionDayClosedPayloadSchema,
  production_day_verified: ProductionDayVerifiedPayloadSchema,
  production_day_reopened: ProductionDayReopenedPayloadSchema,
  production_input_trace: ProductionInputTracePayloadSchema,
  cost_adjustment: CostAdjustmentPayloadSchema,
} as const;

export const EventSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  business_id: z.string().uuid().nullable(),
  kind: EventKindSchema,
  actor_user_id: z.string().uuid(),
  role: z.string(),
  device_id: z.string(),
  client_uuid: z.string().uuid(),
  client_ts: z.string().datetime(),
  server_ts: z.string().datetime(),
  server_rev: z.number().int().positive(),
  payload: z.record(z.unknown()),
  prev_hash: z.string().length(64).nullable(),
  self_hash: z.string().length(64),
  hash_version: z.literal(2).nullable().optional(),
  anchor_id: z.string().uuid().nullable(),
});
export type Event = z.infer<typeof EventSchema>;

export function validateEventPayload(kind: EventKind, payload: unknown): unknown {
  const schema = EventPayloadByKind[kind];
  return schema.parse(payload);
}
